import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronRight, Mail, Phone, MapPin, Send, Instagram, Facebook } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 1000));

    toast.success('Message sent! We\'ll get back to you soon.');
    setFormData({
      name: '',
      email: '',
      phone: '',
      subject: '',
      message: ''
    });
    setLoading(false);
  };

  const contactInfo = [
    { icon: Mail, label: 'Email', value: 'methwani777@gmail.com', href: 'mailto:methwani777@gmail.com' },
    { icon: Phone, label: 'Phone', value: '+91 94081 95777', href: 'tel:+919408195777' },
    { icon: MapPin, label: 'Location', value: 'Godhra, Gujarat, India', href: null },
  ];

  return (
    <div className="min-h-screen">
      {/* Header */}
      <section className="bg-white py-12 md:py-16">
        <div className="container-handcraft">
          <nav className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
            <Link to="/" className="hover:text-charcoal transition-colors">Home</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-charcoal">Contact</span>
          </nav>

          <div className="text-center max-w-2xl mx-auto">
            <p className="font-handwritten text-2xl text-accent mb-2">Get in Touch</p>
            <h1 className="font-serif text-4xl md:text-5xl text-charcoal mb-4">Contact Us</h1>
            <p className="text-muted-foreground">
              Have a question or want to discuss a custom order? We'd love to hear from you!
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="section-spacing pt-0">
        <div className="container-handcraft">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="bg-white rounded-2xl border border-sand p-6 md:p-8">
                <h2 className="font-serif text-2xl text-charcoal mb-6">Send a Message</h2>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label className="label-handcraft">Your Name *</Label>
                      <Input
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        placeholder="John Doe"
                        data-testid="contact-name"
                      />
                    </div>
                    <div>
                      <Label className="label-handcraft">Email *</Label>
                      <Input
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="john@example.com"
                        data-testid="contact-email"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label className="label-handcraft">Phone</Label>
                      <Input
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+91 98765 43210"
                        data-testid="contact-phone"
                      />
                    </div>
                    <div>
                      <Label className="label-handcraft">Subject *</Label>
                      <Input
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                        placeholder="Custom Order Inquiry"
                        data-testid="contact-subject"
                      />
                    </div>
                  </div>

                  <div>
                    <Label className="label-handcraft">Message *</Label>
                    <Textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      placeholder="Tell us about your requirements..."
                      className="min-h-[150px]"
                      data-testid="contact-message"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full btn-handcraft bg-charcoal text-cream hover:bg-charcoal/90"
                    data-testid="contact-submit"
                  >
                    {loading ? 'Sending...' : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Send Message
                      </>
                    )}
                  </Button>
                </form>
              </div>
            </motion.div>

            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="space-y-8"
            >
              <div>
                <p className="font-handwritten text-2xl text-accent mb-4">Let's Connect</p>
                <h2 className="font-serif text-2xl text-charcoal mb-4">Contact Information</h2>
                <p className="text-muted-foreground">
                  We're here to help with any questions about our products, custom orders,
                  or corporate gifting solutions.
                </p>
              </div>

              <div className="space-y-6">
                {contactInfo.map((item) => (
                  <div key={item.label} className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-5 h-5 text-accent" />
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">
                        {item.label}
                      </p>
                      {item.href ? (
                        <a href={item.href} className="text-charcoal hover:text-accent transition-colors">
                          {item.value}
                        </a>
                      ) : (
                        <p className="text-charcoal">{item.value}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Social Links */}
              <div>
                <p className="text-xs uppercase tracking-widest text-muted-foreground mb-4">
                  Follow Us
                </p>
                <div className="flex gap-4">
                  <a
                    href="https://www.instagram.com/yms_creation/" target='blank'
                    className="w-12 h-12 rounded-full bg-charcoal flex items-center justify-center hover:bg-accent transition-colors"
                    aria-label="Instagram"
                  >
                    <Instagram className="w-5 h-5 text-white" />
                  </a>
                </div>
              </div>

              {/* Working Hours */}
              <div className="bg-muted/50 rounded-2xl p-6">
                <h3 className="font-serif text-lg text-charcoal mb-4">Working Hours</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Monday - Friday</span>
                    <span className="text-charcoal">10:00 AM - 7:00 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Saturday</span>
                    <span className="text-charcoal">10:00 AM - 5:00 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Sunday</span>
                    <span className="text-charcoal">Closed</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
